import { useEffect, useId, useRef, type ReactNode } from 'react';
import { IconX } from '@tabler/icons-react';
import { cn } from '../lib/utils';
import { Button } from './button';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Accessible name when `title` is not plain text. */
  ariaLabel?: string;
  description?: ReactNode;
  closeLabel: string;
  /** `max-w-2xl` by default (DESIGN.md); `sm` for confirmations and keypads. */
  size?: 'sm' | 'md' | 'lg';
  /** Prevents backdrop/Esc close while a command is in flight. */
  dismissible?: boolean;
  footer?: ReactNode;
  children?: ReactNode;
}

const widths = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl' } as const;

/**
 * DESIGN.md §6 Modal: native `<dialog>` + `showModal()` (focus trap, Esc, inert page come
 * from the platform). Header with bottom border; body `max-h-[70vh]` scrolls. Backdrop
 * click or Esc closes. Children mount only while open, so form state resets per opening.
 */
export function Modal({
  open,
  onClose,
  title,
  ariaLabel,
  description,
  closeLabel,
  size = 'md',
  dismissible = true,
  footer,
  children,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } else if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabel ? undefined : titleId}
      aria-describedby={description ? descId : undefined}
      onCancel={(e) => {
        e.preventDefault();
        if (dismissible) onClose();
      }}
      onClick={(e) => {
        // A click on the <dialog> element itself (not its content) is a backdrop click.
        if (e.target === e.currentTarget && dismissible) onClose();
      }}
      className={cn(
        'm-auto w-[calc(100vw-32px)] rounded-xl border border-[var(--line)] bg-[var(--surface)] p-0 text-[var(--ink)] shadow-[var(--shadow-modal)]',
        widths[size],
      )}
    >
      {open ? (
        <div className="flex max-h-[calc(100dvh-32px)] flex-col">
          <div className="flex items-start justify-between gap-4 border-b border-[var(--line)] px-6 py-4">
            <div className="min-w-0">
              <h2 id={titleId} className="text-base font-semibold">
                {title}
              </h2>
              {description ? (
                <p id={descId} className="mt-0.5 text-xs text-[var(--ink-soft)]">
                  {description}
                </p>
              ) : null}
            </div>
            <Button
              variant="ghost-icon"
              aria-label={closeLabel}
              onClick={onClose}
              disabled={!dismissible}
              className="-mr-2"
            >
              <IconX size={18} stroke={2} aria-hidden />
            </Button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>
          {footer ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[var(--line)] px-6 py-4">
              {footer}
            </div>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}
